import { useCallback } from "react";
import { useUploadSupportingDocument } from "../../../../../../api/ingredients";
import { DocumentItem } from "./useDocumentManagement";

interface UploadConfig {
  ingredientId: string;
  supplierId: string;
  onDocumentUpdate: (id: string, updates: Partial<DocumentItem>) => void;
}

export const useDocumentUpload = ({
  ingredientId,
  supplierId,
  onDocumentUpdate
}: UploadConfig) => {
  const { mutate: uploadSupportingDocument } = useUploadSupportingDocument();

  const uploadDocument = useCallback(
    async (doc: DocumentItem): Promise<void> => {
      return new Promise<void>((resolve, reject) => {
        uploadSupportingDocument(
          {
            ingredientId,
            supplierId,
            file: doc.file
          },
          {
            onSuccess: (response: any) => {
              onDocumentUpdate(doc.id, {
                isUploading: false,
                uploadedResponse: response
              });
              resolve();
            },
            onError: (error: any) => {
              const errorMsg =
                error?.response?.data?.message || error?.message || "Upload failed";
              onDocumentUpdate(doc.id, {
                isUploading: false,
                uploadError: errorMsg
              });
              reject(error);
            }
          } as any
        );
      });
    },
    [ingredientId, supplierId, uploadSupportingDocument, onDocumentUpdate]
  );

  const uploadMultipleDocuments = useCallback(
    async (docsToUpload: DocumentItem[]) => {
      for (const doc of docsToUpload) {
        try {
          await uploadDocument(doc);
        } catch (error) {
          console.error("Failed to upload document:", error);
          // Continue uploading other documents even if one fails
        }
      }
    },
    [uploadDocument]
  );

  return {
    uploadDocument,
    uploadMultipleDocuments
  };
};
